import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axiosURLS from "../services/Api";

const FONT_MONO = { fontFamily: "'Courier New',monospace" } as const;

const SLOT_KEY_MAP: { [key: string]: number } = {
  TEN_AM_TO_ELEVEN_AM: 10,
  ELEVEN_AM_TO_TWELVE_PM: 11,
  TWELVE_PM_TO_ONE_PM: 12,
  ONE_PM_TO_TWO_PM: 13,
  TWO_PM_TO_THREE_PM: 14,
  THREE_PM_TO_FOUR_PM: 15,
  FOUR_PM_TO_FIVE_PM: 16,
  FIVE_PM_TO_SIX_PM: 17,
  SIX_PM_TO_SEVEN_PM: 18,
  SEVEN_PM_TO_EIGHT_PM: 19,
  EIGHT_PM_TO_NINE_PM: 20,
  NINE_PM_TO_TEN_PM: 21,
  TEN_PM_TO_ELEVEN_PM: 22,
  ELEVEN_PM_TO_TWELVE_AM: 23,
};

function hourParts(h: number): { time: string; period: string } {
  if (h === 12) return { time: "12:00", period: "pm" };
  if (h === 0 || h === 24) return { time: "12:00", period: "am" };
  if (h > 12) return { time: `${h - 12}:00`, period: "pm" };
  return { time: `${h}:00`, period: "am" };
}

function formatTime(time: string): string {
  const [h, min] = time.split(":").map(Number);
  return `${h % 12 || 12}:${String(min).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

function deduplicateRows(rows: any[]): any[] {
  if (rows.length === 0) return rows;
  const result = [{ ...rows[0] }];
  for (let i = 1; i < rows.length; i++) {
    const last = result[result.length - 1];
    if (rows[i].amount === last.amount) { last.toTime = rows[i].toTime; }
    else { result.push({ ...rows[i] }); }
  }
  return result;
}

interface SlotInfo { availableSlots: number; totalSlots: number; }

function Option() {
  const [totalAvailable, setTotalAvailable] = useState<number | null>(null);
  const [todayRates, setTodayRates]         = useState<any[]>([]);
  const [rateLabel, setRateLabel]           = useState("");
  const [rateColor, setRateColor]           = useState("#2d7a4e");
  const [slotStrip, setSlotStrip]           = useState<{ hour: number; info: SlotInfo }[]>([]);

  useEffect(() => {
    const today    = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;
    const isWeekend = today.getDay() === 0 || today.getDay() === 6;
    const currentHour = today.getHours();

    axiosURLS.post("/arena/search", {})
      .then((arenaRes) => {
        const arenas: any[] = arenaRes.data;
        if (arenas.length === 0) return;

        const availPromises = [
          axiosURLS.post("/onlineBooking/getAvailability", {
            fromDate: todayStr, toDate: todayStr, arenaId: arenas[0].id,
          })
          .then(r => r.data?.bookingsAvailabilityInfo?.[todayStr]?.slotwiseAvailability ?? {})
          .catch(() => ({}))
        ];

        const pricingPromise = axiosURLS
          .post("/pricing/search", { arenaId: arenas[0].name })
          .then(r => r.data as any[])
          .catch(() => [] as any[]);

        return Promise.all([Promise.all(availPromises), pricingPromise]);
      })
      .then((results) => {
        if (!results) return;
        const [allSlotwise, allPricing] = results as [any[], any[]];

        // ── Availability total ──
        const firstSlotwise = allSlotwise[0] ?? {};
        const firstEntry = Object.values(firstSlotwise)[0] as any;
        setTotalAvailable(firstEntry?.totalSlots ?? 0);

        // ── Slot strip ──
        const strip: { hour: number; info: SlotInfo }[] = [];
        for (const [key, val] of Object.entries(firstSlotwise)) {
          const hour = SLOT_KEY_MAP[key];
          if (hour !== undefined && hour > currentHour) {
            strip.push({ hour, info: val as SlotInfo });
          }
        }
        strip.sort((a, b) => a.hour - b.hour);
        setSlotStrip(strip);

        // ── Today's rates ──
        const todayDateStr = new Date().toISOString().split("T")[0];
        const isSpecial    = allPricing.some((p: any) => p.date === todayDateStr);
        const type         = isSpecial ? "SPECIAL" : isWeekend ? "WEEKEND" : "NORMAL";
        const label        = isSpecial ? "Special Day Rates" : isWeekend ? "Weekend Rates" : "Weekday Rates";
        const color        = isSpecial ? "#f59e0b" : isWeekend ? "#10b981" : "#2d7a4e";

        const filtered = allPricing
          .filter((p: any) => p.pricingType === type && p.amount > 0 && p.fromTime !== p.toTime)
          .map((p: any) => ({ amount: p.amount, fromTime: p.fromTime, toTime: p.toTime }));
        const rows = deduplicateRows(filtered);

        setRateLabel(label);
        setRateColor(color);
        setTodayRates(rows);
      })
      .catch(() => {});
  }, []);

  return (
    <div
      className="relative overflow-hidden min-h-screen"
      style={{ background: "linear-gradient(180deg, #0f1419 0%, #1a1f29 100%)" }}
    >
      {/* Animated grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.06,
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 35px, rgba(255,255,255,0.05) 35px, rgba(255,255,255,0.05) 36px), repeating-linear-gradient(90deg, transparent, transparent 35px, rgba(255,255,255,0.05) 35px, rgba(255,255,255,0.05) 36px)",
          animation: "patternMove 20s linear infinite",
        }}
      />

      {/* Floating stadium SVG illustration */}
      <div
        className="absolute left-1/2 pointer-events-none"
        style={{ top: "8%", width: "80%", transform: "translateX(-50%)", opacity: 0.12, animation: "float 6s ease-in-out infinite" }}
      >
        <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto" }}>
          <ellipse cx="200" cy="150" rx="180" ry="120" stroke="#2d7a4e" strokeWidth="3" fill="none" />
          <ellipse cx="200" cy="150" rx="150" ry="90" stroke="#1a4d2e" strokeWidth="2" fill="none" opacity="0.5" />
          <rect x="150" y="100" width="100" height="100" fill="#1a4d2e" opacity="0.3" rx="4" />
          <line x1="175" y1="150" x2="175" y2="165" stroke="#FF5A22" strokeWidth="2" />
          <line x1="225" y1="150" x2="225" y2="165" stroke="#FF5A22" strokeWidth="2" />
          <circle cx="80" cy="80" r="8" fill="#FF5A22" opacity="0.6" />
          <line x1="80" y1="80" x2="80" y2="140" stroke="#a0aec0" strokeWidth="2" />
          <circle cx="320" cy="80" r="8" fill="#FF5A22" opacity="0.6" />
          <line x1="320" y1="80" x2="320" y2="140" stroke="#a0aec0" strokeWidth="2" />
          <circle cx="80" cy="220" r="8" fill="#FF5A22" opacity="0.6" />
          <line x1="80" y1="220" x2="80" y2="160" stroke="#a0aec0" strokeWidth="2" />
          <circle cx="320" cy="220" r="8" fill="#FF5A22" opacity="0.6" />
          <line x1="320" y1="220" x2="320" y2="160" stroke="#a0aec0" strokeWidth="2" />
        </svg>
      </div>

      {/* Main content */}
      <motion.div
        className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 py-12"
        style={{ marginTop: "-40px" }}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Branding */}
        <div className="mb-10 text-center w-full flex flex-col items-center">
          <motion.img
            src="/six2o-gold-logo.png"
            alt="Six2O"
            className="h-48 mx-auto mb-5 rounded-2xl object-contain block"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
          <p style={{ color: "#a0aec0", fontSize: "17px" }}>
            Book Your Bay, Own Your Game
          </p>
        </div>

        {/* Action Cards */}
        <div className="flex flex-col gap-3 max-w-sm mx-auto w-full">
          {/* Primary — Book a Bay */}
          <motion.div whileTap={{ scale: 0.97 }}>
            <Link
              to="/booking"
              className="flex items-center justify-between px-6 py-5 rounded-2xl"
              style={{
                background: "linear-gradient(135deg, #FF5A22 0%, #FF8B5E 100%)",
                boxShadow: "0 8px 32px rgba(255, 90, 34, 0.4)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                textDecoration: "none",
              }}
            >
              <div>
                <h2 className="font-black mb-1" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", fontSize: "24px", color: "#0f1419" }}>
                  Book a Bay
                </h2>
                <p style={{ color: "rgba(15,20,25,0.7)", fontSize: "14px" }}>Choose your slot in seconds</p>
              </div>
              <svg width="44" height="44" fill="none" viewBox="0 0 24 24" stroke="rgba(15,20,25,0.75)" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </Link>
          </motion.div>

          {/* Secondary — Check Booking */}
          <motion.div whileTap={{ scale: 0.97 }}>
            <Link
              to="/status"
              className="flex items-center justify-between px-6 py-5 rounded-2xl"
              style={{
                background: "#1a1f29",
                border: "1.5px solid rgba(255,90,34,0.18)",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.50), 0 6px 18px rgba(0,0,0,0.40), 0 16px 40px rgba(0,0,0,0.25)",
                textDecoration: "none",
              }}
            >
              <div>
                <h2 className="font-semibold mb-1" style={{ fontSize: "20px", color: "#ffffff" }}>Check Booking</h2>
                <p style={{ color: "#a0aec0", fontSize: "14px" }}>View your reservations</p>
              </div>
              <svg width="36" height="36" fill="none" viewBox="0 0 24 24" stroke="#2d7a4e" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </Link>
          </motion.div>

          {/* Today's slot availability strip */}
          {slotStrip.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="rounded-2xl overflow-hidden"
              style={{
                border: "1.5px solid rgba(255,90,34,0.22)",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                background: "#1a1f29",
                boxShadow: "0 1px 3px rgba(0,0,0,0.50), 0 6px 18px rgba(0,0,0,0.40)",
              }}
            >
              <div className="px-4 py-2 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ color: "#ffffff", fontSize: 12, fontWeight: 700, letterSpacing: "0.04em" }}>Today's Slots</span>
                <Link to="/booking" style={{ color: "#FF5A22", fontSize: 11, fontWeight: 600, textDecoration: "none" }}>Book →</Link>
              </div>
              <div className="px-4 py-3">
                <div className="flex gap-1.5 flex-wrap">
                  {slotStrip.map(({ hour, info }) => {
                    const booked = info.availableSlots === 0;
                    const low = !booked && info.availableSlots <= 2;
                    return (
                      <Link
                        key={hour}
                        to="/booking"
                        title={booked ? "Fully booked" : `${info.availableSlots} bay${info.availableSlots !== 1 ? "s" : ""} left`}
                        style={{ textDecoration: "none" }}
                      >
                        <div
                          className="flex flex-col items-center rounded-xl px-2 py-1.5 transition-all"
                          style={{
                            background: booked ? "rgba(239,68,68,0.1)" : low ? "rgba(245,158,11,0.12)" : "rgba(16,185,129,0.1)",
                            border: booked ? "1px solid rgba(239,68,68,0.25)" : low ? "1px solid rgba(245,158,11,0.3)" : "1px solid rgba(16,185,129,0.25)",
                            minWidth: 38,
                          }}
                        >
                          {(() => { const { time, period } = hourParts(hour); const clr = booked ? "#ef4444" : low ? "#f59e0b" : "#10b981"; return (
                            <>
                              <span style={{ ...FONT_MONO, fontSize: 10, fontWeight: 700, color: clr, lineHeight: 1.1 }}>{time}</span>
                              <span style={{ ...FONT_MONO, fontSize: 8, fontWeight: 600, color: clr, opacity: 0.8, lineHeight: 1.1 }}>{period}</span>
                            </>
                          ); })()}
                          <div
                            className="rounded-full mt-1"
                            style={{ width: 5, height: 5, background: booked ? "#ef4444" : low ? "#f59e0b" : "#10b981" }}
                          />
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-3">
                  {[
                    { color: "#10b981", label: "Available" },
                    { color: "#f59e0b", label: "Low (≤2)" },
                    { color: "#ef4444", label: "Booked" },
                  ].map(({ color, label }) => (
                    <div key={label} className="flex items-center gap-1">
                      <div className="rounded-full" style={{ width: 6, height: 6, background: color }} />
                      <span style={{ color: "#6b7280", fontSize: 10 }}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Today's Rates card */}
          {todayRates.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="rounded-2xl overflow-hidden"
              style={{
                border: "1.5px solid rgba(255,90,34,0.22)",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                background: "#1a1f29",
                boxShadow: "0 1px 3px rgba(0,0,0,0.50), 0 6px 18px rgba(0,0,0,0.40)",
              }}
            >
              <div className="px-4 py-2 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex items-center gap-2">
                  <div className="rounded-full" style={{ width: 7, height: 7, background: rateColor, flexShrink: 0 }} />
                  <span style={{ color: "#ffffff", fontSize: 12, fontWeight: 700, letterSpacing: "0.04em" }}>{rateLabel}</span>
                </div>
                <span style={{ color: "#6b7280", fontSize: 11 }}>Today</span>
              </div>
              <div className="px-4 py-3">
                {todayRates.map((row: any, i: number) => (
                  <div
                    key={i}
                    className="flex items-center justify-between"
                    style={{
                      paddingTop: i > 0 ? 8 : 0,
                      paddingBottom: i < todayRates.length - 1 ? 8 : 0,
                      borderBottom: i < todayRates.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                    }}
                  >
                    <span style={{ color: "#a0aec0", fontSize: 13 }}>
                      {formatTime(row.fromTime)} – {formatTime(row.toTime)}
                    </span>
                    <div className="flex flex-col items-end">
                      <span style={{ ...FONT_MONO, color: "#FF5A22", fontWeight: 700, fontSize: 14 }}>₹{row.amount}</span>
                      <span style={{ color: "#6b7280", fontSize: 10 }}>per person</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Live availability indicator */}
        <div className="flex items-center justify-center gap-2 mt-6" style={{ color: "#6b7280", fontSize: "14px" }}>
          <div
            className="rounded-full flex-shrink-0"
            style={{
              width: 8, height: 8,
              background: totalAvailable === 0 ? "#ef4444" : "#10b981",
              animation: "pulseDot 2s cubic-bezier(0.4,0,0.6,1) infinite",
            }}
          />
          <span>
            {totalAvailable === null
              ? "Checking availability..."
              : totalAvailable === 0
              ? "No bays available today"
              : `${totalAvailable} Bays available today`}
          </span>
        </div>
      </motion.div>
    </div>
  );
}

export default Option;
